import { useCallback, useEffect, useRef, useState } from 'react';
import { directoryApi } from '../api/calls.js';

/**
 * useDirectoryImportJob(hostId)
 *
 * 管理 /directory 的匯入 job 狀態：
 *   - 上傳檔案 → 拿 jobId → 存 localStorage（key 含 hostId）
 *   - active 時每 2s poll 一次 status
 *   - 提供 confirm / dismiss
 *
 * Job lifecycle (與 backend 對齊)：
 *   created → parsing → preview_ready → committing → done | error
 *
 * 關掉 dialog 不會中斷 job — hook 還在 poll。
 */
const POLL_INTERVAL = 2000;
const STORAGE_KEY_PREFIX = 'qubby-admin:dirImportJob:';

function isActive(job) {
    return job && ['created', 'parsing', 'committing'].includes(job.status);
}

export function useDirectoryImportJob(hostId) {
    const [jobId, setJobId] = useState(() => {
        if (!hostId) return null;
        return localStorage.getItem(STORAGE_KEY_PREFIX + hostId) || null;
    });
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const pollTimer = useRef(null);

    // hostId 換 → 重抓 localStorage
    useEffect(() => {
        if (!hostId) { setJobId(null); setJob(null); return; }
        const saved = localStorage.getItem(STORAGE_KEY_PREFIX + hostId);
        setJobId(saved || null);
    }, [hostId]);

    // poll job status when jobId set
    useEffect(() => {
        if (!jobId) {
            setJob(null);
            if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
            return;
        }
        let cancelled = false;
        const tick = async () => {
            try {
                const r = await directoryApi.importStatus(jobId);
                if (cancelled) return;
                setJob(r.job);
                setError(null);
                if (!isActive(r.job) && pollTimer.current) {
                    clearInterval(pollTimer.current);
                    pollTimer.current = null;
                }
            } catch (e) {
                if (e.status === 404) {
                    // job 過期/被清掉 → 清 storage
                    localStorage.removeItem(STORAGE_KEY_PREFIX + hostId);
                    setJobId(null);
                    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
                } else {
                    setError(e);
                }
            }
        };
        tick();
        pollTimer.current = setInterval(tick, POLL_INTERVAL);
        return () => {
            cancelled = true;
            if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
        };
    }, [jobId, hostId]);

    const start = useCallback(async (file) => {
        if (!hostId || !file) return;
        setLoading(true);
        setError(null);
        try {
            const r = await directoryApi.importStart(hostId, file);
            localStorage.setItem(STORAGE_KEY_PREFIX + hostId, r.jobId);
            setJobId(r.jobId);
            return r.jobId;
        } catch (e) {
            setError(e);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [hostId]);

    const confirm = useCallback(async (contacts) => {
        if (!jobId) return;
        setLoading(true);
        try {
            return await directoryApi.importConfirm(jobId, contacts);
        } catch (e) {
            setError(e);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [jobId]);

    const dismiss = useCallback(async () => {
        if (!jobId) return;
        try { await directoryApi.importDismiss(jobId); } catch {}
        localStorage.removeItem(STORAGE_KEY_PREFIX + hostId);
        setJobId(null);
        setJob(null);
    }, [jobId, hostId]);

    return { jobId, job, loading, error, start, confirm, dismiss, isActive: isActive(job) };
}
